'use client'
import { SbBlokData, storyblokEditable } from '@storyblok/react/rsc'
import { useSearchParams } from 'next/navigation'
import { FC, useEffect, useState } from 'react'
import StoryblokClient from 'storyblok-js-client'

import { PhnxButton } from '@/components/01-atoms/Button/Button'
import { PhnxImageHolder } from '@/components/01-atoms/ImageHolder/ImageHolder'
import { PhnxCard } from '@/components/02-molecules/Card/Card'
import { setDate } from '@/lib/date'
import { calculateReadingTime } from '@/lib/readingTime'
import { searchParamsData } from '@/lib/sbAPI'
import { getStoryblokApi } from '@/lib/storyblok'

import styles from './Search.module.css'

export interface SearchSchema extends SbBlokData {
  header: any
  body: any
  Search_availability: any
}

interface SearchProps {
  blok: SearchSchema
}

const Search: FC<SearchProps> = ({ blok }) => {
  const [results, setResults] = useState<any[]>([])
  const [pages, setPages] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [topics, setTopics] = useState<any[]>([])
  const [types, setTypes] = useState<any[]>([])
  const [selectedTopic, setSelectedTopic] = useState('all')
  const [selectedContentType, setSelectedContentType] = useState('all')
  const [selectedSort, setSelectedSort] = useState('relevance')
  const [searchText, setSearchText] = useState('')
  const [noResults, setNoResults] = useState<boolean>(false)

  const Storyblok = new StoryblokClient({ accessToken: process.env.NEXT_PUBLIC_SB_PREVIEW_TOKEN })

  const urlParams = useSearchParams()

  const ppp = 28 // items per page
  const location = process.env.NEXT_PUBLIC_NEXT_LOCATION || 'au'
  const filterLocation = location === 'en-nz' ? 'au' : 'en-nz'

  useEffect(() => {
    const initialTopic = urlParams.get('topic') || 'all'
    const initialContentType = urlParams.get('contentType') || 'all'
    const initialSort = urlParams.get('sort') || 'relevance'
    const initialSearch = urlParams.get('search') || ''

    setSelectedTopic(initialTopic)
    setSelectedContentType(initialContentType)
    setSelectedSort(initialSort)
    setSearchText(initialSearch)

    // Fetch articles with initial params
    fetchArticles(1, initialTopic, initialContentType, initialSort, initialSearch)
  }, [urlParams])

  const formatResults = (data: any) => {
    // Validate required fields
    if (!data?.content?.title || !data?.full_slug) {
      console.warn('Invalid story data:', data)
      return null
    }

    const result = {
      title: data.content.title,
      date: setDate(data.content.date),
      timeToRead: ArticleReadTime(data.content.body),
      link: '/' + data.full_slug,
      link_label: 'Read story',
      asset: data.content.card_asset
        ? {
            src: data.content.card_asset.filename,
            alt: data.content.card_asset.alt || '',
            focus: data.content.card_asset.focus || '',
          }
        : data.content.featured_image
          ? {
              src: data.content.featured_image.filename,
              alt: data.content.featured_image.alt || '',
              focus: data.content.featured_image.focus || '',
            }
          : null,
      tags: data.content.tags || [],
      insightType: (data.content.insight_type || data.content.article_type || '').toLowerCase(),
      articleType: data.content.article_type || '',
    }

    // Validate the result has at least a title and link
    if (!result.title || !result.link) {
      console.warn('Invalid result format:', result)
      return null
    }

    return result
  }
  // TODO: This should be reusable and moved to a helper file
  function ArticleReadTime(body: any[] | undefined) {
    // Check if body exists and has content
    if (!body || !body.length || !body[0]?.content) {
      return '0 min read'
    }

    try {
      const html = Storyblok.richTextResolver.render(body[0].content)
      return calculateReadingTime(html)
    } catch (error) {
      console.error('Error calculating reading time:', error)
      return '0 min read'
    }
  }

  const fetchTopics = async () => {
    const storyblokApi = getStoryblokApi()
    const response = await storyblokApi.get('cdn/datasource_entries', {
      datasource: 'article-tags',
      token: process.env.NEXT_PUBLIC_SB_PREVIEW_TOKEN,
      per_page: 100,
    })
    setTopics(response.data.datasource_entries)
  }

  const fetchTypes = async () => {
    const storyblokApi = getStoryblokApi()
    const response = await storyblokApi.get('cdn/datasource_entries', {
      datasource: 'article-type',
      token: process.env.NEXT_PUBLIC_SB_PREVIEW_TOKEN,
      per_page: 100,
    })
    response.data.datasource_entries = response.data.datasource_entries.map((type: any) => {
      type.title = type.name + 's'
      return type
    })
    setTypes(response.data.datasource_entries)
  }

  useEffect(() => {
    fetchTopics()
    fetchTypes()
  }, [])

  const getFilterQuery = () => ({
    component: { in: 'article' },
    __or: [
      { 'settings.location_available': { in: location } },
      { 'settings.location_available': { not_in: filterLocation } },
    ],
  })

  const buildParams = (
    page: number,
    topic: string,
    contentType: string,
    sort: string,
    search: string
  ) => {
    let params: any = {
      ...searchParamsData,
      filter_query: getFilterQuery(),
      per_page: ppp,
      page: page,
    }

    if (topic !== 'all') {
      params.filter_query.tags = { any_in_array: topic }
    }

    if (contentType !== 'all') {
      params.filter_query.article_type = { in: contentType }
    }

    if (search) {
      params.search_term = search
    }

    params.sort_by =
      sort === 'relevance'
        ? 'content.relevance'
        : sort === 'datedesc'
          ? 'content.date:desc'
          : 'content.date:asc'

    return params
  }

  const fetchArticles = async (
    page: number,
    topic: string = selectedTopic,
    contentType: string = selectedContentType,
    sort: string = selectedSort,
    search: string = searchText
  ) => {
    const storyblokApi = getStoryblokApi()
    const params = buildParams(page, topic, contentType, sort, search)

    try {
      const { data, headers }: { data: any; headers: any } = await storyblokApi.get(
        'cdn/stories/',
        params
      )

      const total = headers.total
      setPages(Math.ceil(total / ppp))
      setCurrentPage(page)

      if (data?.stories && data.stories.length > 0) {
        const formattedResults = data.stories
          .map((story: any) => {
            try {
              return formatResults(story)
            } catch (error) {
              console.error('Error formatting story:', story.id, error)
              return null
            }
          })
          .filter((result: any) => result !== null) // Remove any null results

        setResults((prevResults) => {
          const newResults = page === 1 ? formattedResults : [...prevResults, ...formattedResults]
          return newResults
        })
        setNoResults(false)
      } else {
        setNoResults(true)
        setResults([])
      }
    } catch (error) {
      console.error('Error fetching articles:', error)
      setNoResults(true)
      setResults([])
    }
  }

  const handleSearch = () => {
    // Update URL params
    const newUrlParams = new URLSearchParams()
    newUrlParams.set('topic', selectedTopic)
    newUrlParams.set('contentType', selectedContentType)
    newUrlParams.set('sort', selectedSort)
    if (searchText) {
      newUrlParams.set('search', searchText)
    }
    window.history.replaceState({}, '', `${window.location.pathname}?${newUrlParams}`)

    // Fetch articles with current state values
    fetchArticles(1, selectedTopic, selectedContentType, selectedSort, searchText)
  }

  const loadMoreResults = () => {
    fetchArticles(currentPage + 1, selectedTopic, selectedContentType, selectedSort, searchText)
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
    setState: React.Dispatch<React.SetStateAction<string>>
  ) => {
    setState(e.target.value)
    // Remove automatic search trigger
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
    // Remove automatic search trigger
  }

  return (
    <div {...storyblokEditable(blok)}>
      <div className={`${styles.header}`}>
        <div className="container">
          <div className={styles.title}>
            <h1>Search results</h1>
          </div>
          <div className={styles.filter}>
            <div className={styles.item}>
              <label htmlFor="topics">Topics</label>
              <select
                name="topic"
                id="topics"
                value={selectedTopic}
                onChange={(e) => handleChange(e, setSelectedTopic)}
              >
                <option value="all">All</option>
                {topics.map((topic, index) => (
                  <option key={index} value={topic.name}>
                    {topic.name}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.item}>
              <label htmlFor="content-type">Content Type</label>
              <select
                name="contentType"
                id="content-type"
                value={selectedContentType}
                onChange={(e) => handleChange(e, setSelectedContentType)}
              >
                <option value="all">All</option>
                {types.map((type, index) => (
                  <option key={index} value={type.name}>
                    {type.title}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.item}>
              <label htmlFor="sort">Sort</label>
              <select
                name="sort"
                id="sort"
                value={selectedSort}
                onChange={(e) => handleChange(e, setSelectedSort)}
              >
                <option value="relevance">Relevance</option>
                <option value="datedesc">Date Desc</option>
                <option value="dateasc">Date Asc</option>
              </select>
            </div>
            <div className={styles.item}>
              <label htmlFor="freetext" className="sr-only">
                Free Text
              </label>
              <input
                type="text"
                name="freetext"
                id="freetext"
                placeholder="Search for insights"
                value={searchText}
                onChange={handleTextChange}
              />
            </div>
            <div className={styles.search}>
              <PhnxButton
                type="button"
                label="Search"
                icon={'fx-icon-search'}
                onClick={handleSearch}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={`${styles.results} container`}>
        {results.map((result) => (
          <PhnxCard
            key={`${result.title}-${result.link}`} // Use a unique combination of properties
            variant="senary"
            layout="vertical"
            alignment="default"
            title={result.title}
            subtitle={
              <div>
                <div className="date">{result.date}</div>
                <div className="timetoread">{result.timeToRead}</div>
              </div>
            }
            link={result.link}
            button={
              <PhnxButton
                type="link"
                href={result.link}
                variant="quinary"
                label={result.link_label}
              />
            }
            asset={
              result.asset && (
                <PhnxImageHolder
                  key={result.asset.src} // Add this line
                  images={[
                    { src: result.asset.src, alt: result.asset.alt, focus: result.asset.focus },
                  ]}
                  maximumHeight={350}
                  maximumWidth={650}
                />
              )
            }
            tags={result.tags}
            // check for type
            insightType={result?.insightType ? result?.insightType.toLowerCase() : ''}
          />
        ))}
      </div>
      {noResults && (
        <div className={styles['noResults']}>
          <p>No results found</p>
        </div>
      )}
      {currentPage < pages && (
        <div className={`${styles.loadmore} container`}>
          <PhnxButton
            type="button"
            variant="tertiary"
            label="View more"
            icon={'fx-icon-angle-small-down'}
            iconPosition="right"
            onClick={loadMoreResults}
          />
        </div>
      )}
    </div>
  )
}

export default Search
